import { InsuranceCarrier } from '@provider-directory/types';
import {
  CLIENT_ORG_CONTEXT_ENUM,
  ClientOrgContextKey,
  ContextKeySchema,
} from '@provider-directory/types/organizations.contracts';
import {
  ProviderDetailsResource,
  ProviderSpecialtyResource,
  Status,
} from '@provider-directory/types/providers.contracts';
import type {
  BaseInsuranceListRule,
  DisplayContactProviderForInsuranceRule,
  InsuranceResolutionRule,
  RuntimeInsurance,
} from '@provider-directory/types/runtime-config.contracts';

const DENTISTRY_SPECIALTIES = [
  'dentistry',
  'pediatric dentistry',
  'endodontics',
  'orthodontics',
  'periodontics',
  'prosthodontics',
  'oral and maxillofacial surgery',
];

export const shouldHideAcceptedInsurances = (
  specialty?: ProviderSpecialtyResource,
): boolean => {
  return (
    !!specialty && DENTISTRY_SPECIALTIES.includes(specialty.name.toLowerCase())
  );
};

export interface InsuranceResolutionContext {
  hospitalAssociationMapping?: Record<string, string>;
  insuranceResolutionRule?: InsuranceResolutionRule;
  /**
   * Family-wide carrier data from runtime config, keyed by directory key.
   * Resolution can land on a sibling directory, so this is the whole family's
   * map (inherited from the customer umbrella), not just the active directory's.
   */
  insurance?: RuntimeInsurance;
  /**
   * The directory serving the page. Used to label warnings and as the default
   * for the specialty-override path — never to pick the base carrier list, which
   * is decided by `insurance.baseListRule` from the provider alone.
   */
  fallbackKey: ClientOrgContextKey;
}

/**
 * Directory+field pairs already warned about missing `insurance` data.
 * Module-scoped so the warning fires at most once per pair per process (SSR) or
 * session (browser), not once per render.
 *
 * Keyed by directory AND field: two absent fields on the same directory are two
 * separate seeding gaps, so deduping on the directory alone would silence the
 * second one.
 */
const warnedMissingInsuranceData = new Set<string>();

/** `path` is the full config path, so the warning names exactly what is missing. */
const warnMissingInsuranceDataOnce = (
  fallbackKey: ClientOrgContextKey,
  path: string,
): void => {
  const seenKey = `${fallbackKey}:${path}`;
  if (warnedMissingInsuranceData.has(seenKey)) {
    return;
  }
  warnedMissingInsuranceData.add(seenKey);
  console.warn(
    `[insuranceModel] ${path} is absent for directory "${fallbackKey}"; serving an empty list.`,
  );
};

/**
 * Picks the directory whose carrier list applies to this provider.
 *
 * Walks `precedence` in order and returns the first matching entry; `default`
 * answers when none matches, including a provider carrying no associations.
 *
 * The browsing directory is deliberately not an input. A carrier list belongs to
 * the provider, so one provider shows the same carriers on every host — passing
 * the active directory in is what made the same profile differ between MMC, WPH
 * and MEAC.
 */
const resolveBaseInsuranceDirectory = (
  hospitalAssociations: ProviderDetailsResource['hospitalAssociations'],
  rule: BaseInsuranceListRule,
): string => {
  const providerCodes = hospitalAssociations.map((h) => h.code);

  const matched = rule.precedence.find((entry) =>
    entry.match === 'only'
      ? providerCodes.length > 0 &&
        providerCodes.every((code) => entry.codes.includes(code))
      : providerCodes.some((code) => entry.codes.includes(code)),
  );

  return matched?.directory ?? rule.default;
};

export const getProviderAvailableInsurances = (
  hospitalAssociations?: ProviderDetailsResource['hospitalAssociations'],
  context?: InsuranceResolutionContext,
): string[] => {
  if (!hospitalAssociations || !context) {
    return [];
  }

  const rule = context.insurance?.baseListRule;
  if (rule === undefined) {
    // Name the whole block when none of it arrived — "baseListRule is absent"
    // would point at one field when the real gap is the entire insurance config.
    warnMissingInsuranceDataOnce(
      context.fallbackKey,
      context.insurance === undefined ? 'insurance' : 'insurance.baseListRule',
    );
    return [];
  }

  const directory = resolveBaseInsuranceDirectory(hospitalAssociations, rule);

  // The runtime config is the only source. No entry for the resolved directory
  // means no carrier list is configured — show none rather than inventing one.
  const fromConfig = context.insurance?.acceptedInsurances?.[directory];
  if (fromConfig === undefined) {
    warnMissingInsuranceDataOnce(
      context.fallbackKey,
      `insurance.acceptedInsurances.${directory}`,
    );
  }
  return fromConfig ?? [];
};

/**
 * Directories already warned about a missing `insuranceResolutionRule`.
 * Module-scoped to limit log noise to at most one warning per directory per
 * process (SSR) or session (browser).
 */
const warnedMissingInsuranceRule = new Set<ClientOrgContextKey>();

export const getProviderAvailableInsurancesOverrideByOrg = (
  providerHospitalAssociations: ProviderDetailsResource['hospitalAssociations'],
  specialties: ProviderSpecialtyResource[],
  status: Status | null,
  context?: InsuranceResolutionContext,
): InsuranceCarrier[] | null => {
  const fallbackKey =
    context?.fallbackKey ?? CLIENT_ORG_CONTEXT_ENUM.enum.montefiore_mmc;
  const rule = context?.insuranceResolutionRule;

  if (!context || !rule) {
    // No rule configured ⇒ no specialty override applies.
    if (!warnedMissingInsuranceRule.has(fallbackKey)) {
      warnedMissingInsuranceRule.add(fallbackKey);
      console.warn(
        `[insuranceModel] insuranceResolutionRule absent for directory "${fallbackKey}"; no specialty overrides applied.`,
      );
    }
    return null;
  }

  // The rule's `precedence` defines which association codes to try first
  // (e.g. ["meac", "wph", "mmc"]). For each code in order, if the provider has
  // that association AND the mapped directory has specialtyInsuranceOverrides,
  // use that directory. This preserves MEAC-priority for dual-association
  // providers.
  const mapping = context.hospitalAssociationMapping ?? {};
  const providerCodes = new Set<string>(
    providerHospitalAssociations.map((h) => h.code),
  );

  for (const code of rule.precedence) {
    if (!providerCodes.has(code)) {
      continue;
    }
    const mapped = mapping[code];
    if (!mapped) {
      console.warn(
        `[insuranceModel] precedence code "${code}" missing from hospitalAssociationMapping; skipping`,
      );
      continue;
    }
    const parsed = ContextKeySchema.safeParse(mapped);
    if (!parsed.success) {
      console.warn(
        `[insuranceModel] invalid directory key "${mapped}" for code "${code}"`,
      );
      continue;
    }
    // Overrides for the RESOLVED directory. Absent means that directory defines
    // none (true for MMC and WPH) — try the next precedence code.
    const overrides =
      context.insurance?.specialtyInsuranceOverrides?.[parsed.data];
    if (!overrides) {
      continue;
    }

    if (status === null || !rule.requiredStatuses.includes(status)) {
      return null;
    }

    return findMatchingOverrideCarriers(overrides, specialties);
  }

  return null;
};

/**
 * Finds the first override whose specialties intersect with the provider's
 * specialties (case-insensitive).
 */
const findMatchingOverrideCarriers = (
  overrides: { specialties: string[]; carriers: InsuranceCarrier[] }[],
  specialties: ProviderSpecialtyResource[],
): InsuranceCarrier[] | null => {
  const providerSpecialtyNames = new Set(
    specialties.map((s) => s.name.toLowerCase()),
  );

  const matchingOverride = overrides.find((override) =>
    override.specialties.some((specialty) =>
      providerSpecialtyNames.has(specialty.toLowerCase()),
    ),
  );

  return matchingOverride?.carriers ?? null;
};

/**
 * Determines whether the "contact provider for insurance" copy should be shown
 * instead of the normal insurance list. Fully config-driven (association codes ×
 * statuses); an absent rule means the CTA never shows.
 */
export const shouldContactProviderForInsurance = (
  provider: Pick<ProviderDetailsResource, 'hospitalAssociations' | 'status'>,
  rule: DisplayContactProviderForInsuranceRule | undefined,
): boolean => {
  if (!rule) {
    return false;
  }
  const hasMatchingAssociation = provider.hospitalAssociations.some((h) =>
    rule.associationCodes.includes(h.code),
  );
  const hasMatchingStatus =
    provider.status !== null && rule.statuses.includes(provider.status);
  return hasMatchingAssociation && hasMatchingStatus;
};
